import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCodeModal from "../QRCodeModal/QRCodeModal";
import { urlifyName } from "../../shared/Utils/PageUrl";
import CampaignModal from "../CampaignModal/CampaignModal";
import { deleteCampaign } from "../../../api/Campaigns";
import EmailTemplateModal from "../EmailTemplateModal/EmailTemplateModal";
import EditCampaignModal from "../EditCampaignModal/EditCampaignModal";

const CARDS_PER_PAGE = 4;

const ImagePlaceholder = () => (
  <div className="w-full h-full bg-gradient-to-b from-gray-200 to-gray-300 flex items-center justify-center">
    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  </div>
);

const CampaignsComponent = ({
  npData,
  localUserData,
  campaigns,
  setCampaigns,
  fetchCampaigns,
  ifCampaignExists,
  headerCampaignType,
  setHeaderCampaignType,
  onOpenCampaignDetails,
}) => {
  const [campaignModal, setCampaignModal] = useState(false);
  const [campaignType, setCampaignType] = useState("");
  const [updatingCampaignId, setUpdatingCampaignId] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [QRModal, setQRModal] = useState(false);
  const [campaignUrl, setCampaignUrl] = useState("");
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({});
  const [page, setPage] = useState(1);
  const [carouselPage, setCarouselPage] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  const navigate = useNavigate();

  // Opens create campaign modal when triggered from the header button
  useEffect(() => {
    if (headerCampaignType) {
      handleOpenModal(headerCampaignType);
      setHeaderCampaignType("");
    }
  }, [headerCampaignType]);

  // Stops the background from scrolling if a modal is open
  useEffect(() => {
    if (campaignModal || emailModal || updatingCampaignId) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [campaignModal, emailModal, updatingCampaignId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
      setConfirmDelete({});
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleOpenModal = (selectedType) => {
    setCampaignType(selectedType);
    setCampaignModal(true);
  };

  const handleCloseModal = () => {
    setCampaignModal(false);
    setCampaignType(null);
  };

  const handleOpenEmailModal = (campaignId) => {
    const campaign = campaigns.find((c) => c.id === campaignId);
    setSelectedCampaign(campaign);
    setEmailModal(true);
  };

  const handleCloseEmailModal = () => {
    setEmailModal(false);
    setSelectedCampaign(null);
  };

  const handleOpenModify = (campaignId) => setUpdatingCampaignId(campaignId);
  const handleCloseModify = () => setUpdatingCampaignId(null);

  const redirectToCampaignPage = (campaign) => {
    const campaignPath =
      campaign.campaign_type === "animal"
        ? `/campaigns/animal/${urlifyName(campaign.nonprofit_name)}/${urlifyName(campaign.animal_name)}/${campaign.id}`
        : `/campaigns/${campaign.campaign_type}/${urlifyName(campaign.nonprofit_name)}/${urlifyName(campaign.campaign_title)}/${campaign.id}`;
    navigate(campaignPath);
  };

  const handleDeleteConfirm = (id, campaignType) => {
    if (confirmDelete[id]) {
      handleDeleteCampaign(id, campaignType);
    } else {
      setConfirmDelete((prev) => ({ ...prev, [id]: true }));
    }
  };

  const handleDeleteCampaign = async (campaignId, campaignType) => {
    try {
      await deleteCampaign(parseInt(campaignId), campaignType);
      setCampaigns(campaigns.filter((c) => c.id !== campaignId));
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  const handleGenerateQRCode = (campaign) => {
    const { protocol, host } = window.location;
    const campaignPath =
      campaign.campaign_type === "animal"
        ? `animal/${urlifyName(campaign.nonprofit_name)}/${urlifyName(campaign.animal_name)}`
        : `${campaign.campaign_type}/${urlifyName(campaign.nonprofit_name)}/${urlifyName(campaign.campaign_title)}`;
    setCampaignUrl(`${protocol}//${host}/campaigns/${campaignPath}/${campaign.id}`);
    setQRModal(true);
  };

  const handleCloseQRCodeModal = () => {
    setQRModal(false);
    setCampaignUrl("");
  };

  // Carousel navigation
  const visibleCampaigns = campaigns.slice(
    carouselPage * CARDS_PER_PAGE,
    (carouselPage + 1) * CARDS_PER_PAGE
  );
  const canGoPrev = carouselPage > 0;
  const canGoNext =
    (carouselPage + 1) * CARDS_PER_PAGE < campaigns.length || ifCampaignExists;

  const handleNext = () => {
    const nextSliceStart = (carouselPage + 1) * CARDS_PER_PAGE;
    if (nextSliceStart >= campaigns.length && ifCampaignExists) {
      fetchCampaigns(page + 1, "", localUserData.nonprofit, "all");
      setPage((p) => p + 1);
    }
    setCarouselPage((p) => p + 1);
  };

  const handlePrev = () => setCarouselPage((p) => p - 1);

  const isAdminOrEditor =
    localUserData?.np_access_level === "admin" ||
    localUserData?.np_access_level === "editor";

  return (
    <div className="bg-white rounded-2xl shadow-[0_0_20px_rgba(0,0,0,0.08)] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="!font-poppins !font-semibold !text-[22px] text-gray-900 !m-0">
          Your Campaigns
        </h2>
        <div className="flex gap-2">
          <button
            onClick={handlePrev}
            disabled={!canGoPrev}
            className="w-9 h-9 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className="w-9 h-9 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-gray-400 disabled:opacity-30 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Not approved */}
      {localUserData && !npData.is_approved && (
        <p className="text-gray-500 text-sm">
          Your application is under review. In the meantime, check out the rest of your dashboard!
        </p>
      )}

      {/* Campaign cards */}
      {npData.is_approved && (
        <div className="grid grid-cols-4 gap-4">
          {visibleCampaigns
            .map((item) => {
              const progressPct = item.donation_goal > 0
                ? Math.min((item.donation_amount / item.donation_goal) * 100, 100)
                : 0;

              return (
                <div
                  key={item.id}
                  className="rounded-2xl border border-gray-100 shadow-sm bg-white flex flex-col relative"
                >
                  {/* Image area */}
                  <div className="relative h-44 overflow-hidden rounded-t-2xl">
                    {item.campaign_images?.[0] ? (
                      <img
                        src={item.campaign_images[0].image_url}
                        alt={item.campaign_title}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => onOpenCampaignDetails && onOpenCampaignDetails(item)}
                      />
                    ) : (
                      <div
                        className="w-full h-full cursor-pointer"
                        onClick={() => onOpenCampaignDetails && onOpenCampaignDetails(item)}
                      >
                        <ImagePlaceholder />
                      </div>
                    )}
                  </div>

                  {/* Three-dot menu — outside overflow-hidden image div */}
                  {isAdminOrEditor && (
                    <div className="absolute top-2 right-2 z-30">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === item.id ? null : item.id);
                        }}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-600 font-bold text-sm"
                      >
                        •••
                      </button>
                      {openMenuId === item.id && (
                        <div
                          className="absolute right-0 top-8 bg-white border border-gray-200 rounded-xl shadow-lg py-1 min-w-[140px] z-30"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => { setOpenMenuId(null); redirectToCampaignPage(item); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            View
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); onOpenCampaignDetails && onOpenCampaignDetails(item); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => { setOpenMenuId(null); handleGenerateQRCode(item); }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            Share
                          </button>
                          <button
                            onClick={() => {
                              if (confirmDelete[item.id]) setOpenMenuId(null);
                              handleDeleteConfirm(item.id, item.campaign_type);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-50"
                          >
                            {confirmDelete[item.id] ? "Confirm Delete?" : "Delete"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Card body */}
                  <div className="p-4 flex flex-col gap-2">
                    <p className="!font-poppins !font-bold !text-[14px] text-gray-900 !m-0">
                      {item.animal_name || item.campaign_title}
                    </p>
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div
                        className="bg-orange h-1.5 rounded-full"
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <p className="!text-[12px] text-gray-500 !m-0">
                      ${item.donation_amount} of ${item.donation_goal} raised!
                    </p>
                  </div>

                  <EditCampaignModal
                    campaignId={item.id}
                    campaignData={item}
                    isOpen={updatingCampaignId === item.id}
                    onClose={handleCloseModify}
                  />
                </div>
              );
            })}

          {campaigns.length === 0 && (
            <div className="col-span-4 text-center text-gray-400 py-8">
              No campaigns found!
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <QRCodeModal
        isOpen={qrModalOpen}
        onClose={() => { setQrModalOpen(false); setCampaignModal(false); window.location.reload(); }}
        campaignUrl={campaignUrl}
      />

      {campaignModal && (
        <CampaignModal
          nonprofitName={npData.org_name}
          nonprofitId={npData.id}
          isOpen={campaignModal}
          onClose={handleCloseModal}
          setCampaigns={setCampaigns}
          setQrModalOpen={(boolean) => setQrModalOpen(boolean)}
          campaignUrl={campaignUrl}
          setCampaignUrl={setCampaignUrl}
          campaignType={campaignType}
          handleGenerateQRCode={handleGenerateQRCode}
        />
      )}

      <EmailTemplateModal
        isOpen={emailModal}
        onClose={handleCloseEmailModal}
        campaignId={selectedCampaign?.id}
        nonprofitName={npData?.org_name}
        animalName={selectedCampaign?.animal_name}
        nonprofitId={npData?.id}
        customEmailPermission={npData?.custom_emails}
      />

      {QRModal && (
        <QRCodeModal
          isOpen={QRModal}
          onClose={handleCloseQRCodeModal}
          campaignUrl={campaignUrl}
        />
      )}
    </div>
  );
};

export default CampaignsComponent;
